import React from "react";
import {
  X,
  MapPin,
  ShoppingCart,
  Package,
  CheckCircle2,
  Truck,
} from "lucide-react";
import type { CheckoutModalProps } from "./types";

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  show,
  cartItems,
  addresses,
  selectedAddress,
  hasAddresses,
  itemsTotal,
  itemsDiscount,
  subtotal,
  shippingCost,
  estimatedTotal,
  isLoading,
  onClose,
  onSelectAddress,
  onConfirmOrder,
  onContinueShopping,
}) => {
  if (!show) return null;

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-neutral-900 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-neutral-800 animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="relative overflow-hidden bg-primary p-6">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-black/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Package className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-black">Checkout</h2>
                <p className="text-sm text-black/70 mt-1">
                  One last step to complete your order
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-black/10 rounded-xl transition-all duration-200"
            >
              <X className="w-6 h-6 text-black" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Address Selection */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3 text-white">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                Shipping Address
              </h3>

              {hasAddresses ? (
                <div className="space-y-3">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      onClick={() => onSelectAddress(address)}
                      className={`relative group p-5 border rounded-2xl cursor-pointer transition-all duration-200 ${
                        selectedAddress?.id === address.id
                          ? "border-primary bg-primary/5 shadow-lg"
                          : "border-neutral-800 hover:border-neutral-700 hover:shadow-md bg-neutral-800/50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-white text-lg mb-1">
                            {address.address_type_value || "Address"}
                          </div>
                          <div className="text-sm text-neutral-300 font-medium">
                            {address.street}
                          </div>
                          <div className="text-sm text-neutral-400 mt-1">
                            {address.city}
                          </div>
                          {address.building && (
                            <div className="text-xs text-neutral-500 mt-1">
                              Building {address.building}
                              {address.floor && `, Floor ${address.floor}`}
                              {address.apartment &&
                                `, Apt ${address.apartment}`}
                            </div>
                          )}
                        </div>
                        {selectedAddress?.id === address.id && (
                          <div className="flex items-center justify-center">
                            <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-md">
                              <CheckCircle2 className="w-4 h-4 text-black" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 border-2 border-dashed border-neutral-700 rounded-2xl bg-neutral-800/50">
                  <div className="relative inline-block mb-3">
                    <div className="absolute inset-0 bg-neutral-600 rounded-full blur-xl opacity-30"></div>
                    <MapPin className="relative w-14 h-14 text-neutral-500 mx-auto" />
                  </div>
                  <p className="text-neutral-300 font-medium">
                    No addresses available
                  </p>
                  <p className="text-sm text-neutral-500 mt-1">
                    Please add a shipping address
                  </p>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3 text-white">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-primary" />
                </div>
                Order Summary
                <span className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                  {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
                </span>
              </h3>

              <div className="bg-neutral-800 rounded-2xl p-4 max-h-64 overflow-y-auto border border-neutral-700">
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 bg-neutral-900 p-3 rounded-xl"
                    >
                      <div className="relative group">
                        <img
                          src={item.image || "/api/placeholder/60/60"}
                          alt={item.name}
                          className="w-14 h-14 object-cover rounded-lg"
                        />
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-black text-xs font-bold rounded-full flex items-center justify-center shadow-md">
                          {item.cart_quantity}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white truncate">
                          {item.name}
                        </div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                          ${item.usd_price} {item.cart_quantity}
                        </div>
                      </div>
                      <div className="text-base font-bold text-primary">
                        {formatCurrency(
                          (item.usd_price || 0) * item.cart_quantity,
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="relative overflow-hidden bg-neutral-800 rounded-2xl p-6 border border-neutral-700">
              <div className="space-y-3">
                <div className="flex justify-between text-base">
                  <span className="text-neutral-400">Items total</span>
                  <span className="line-through text-neutral-500 font-medium">
                    {formatCurrency(itemsTotal)}
                  </span>
                </div>
                {itemsDiscount > 0 && (
                  <div className="flex justify-between text-base">
                    <span className="text-green-400 font-medium">
                      Items discount
                    </span>
                    <span className="text-green-400 font-bold">
                      -{formatCurrency(itemsDiscount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-base">
                  <span className="text-neutral-400">Subtotal</span>
                  <span className="font-semibold text-white">
                    {formatCurrency(subtotal)}
                  </span>
                </div>
                <div className="flex justify-between text-base items-center">
                  <span className="text-neutral-400 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-primary" />
                    Shipping
                  </span>
                  <span className="font-semibold text-white">
                    {formatCurrency(shippingCost)}
                  </span>
                </div>
                <div className="border-t border-neutral-600 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-white">Total</span>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(estimatedTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={onContinueShopping}
                className="flex-1 border border-neutral-700 text-neutral-300 py-4 rounded-xl font-bold hover:bg-neutral-800 hover:border-neutral-600 transition-all duration-200 shadow-sm"
              >
                Continue Shopping
              </button>
              <button
                onClick={onConfirmOrder}
                disabled={isLoading || !selectedAddress || !selectedAddress.id}
                className="flex-1 bg-primary text-black py-4 rounded-xl font-bold hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 relative overflow-hidden group"
              >
                <span className="relative z-10">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                      Processing...
                    </span>
                  ) : (
                    "Confirm Order"
                  )}
                </span>
                <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
